import { forwardRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Info, ChevronDown, ChevronUp, Lock, Shield, Server, FileCheck, Layers, Sparkles } from "lucide-react";
import { AIProgressRing } from "@/components/ui/ai-progress";
import PercentileBand from "@/components/benchmark/PercentileBand";
import BenchmarkComparison from "@/components/benchmarkComparison";
import TISCMaturityCard from "@/components/TISCMaturityCard";
import HomepageCheckNote from "@/components/HomepageCheckNote";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { describeRank, formatMarks } from "@/lib/benchmarks";
import { getAnalysisPeerComparison } from "@/lib/peer-benchmarks";
import showdown from "showdown";
import DOMPurify from "dompurify";

const contextConverter = new showdown.Converter({
  simpleLineBreaks: true,
  strikethrough: true,
  tables: false,
});

// context_summary is model output steered by the uploaded PDF and shared via
// the analysis cache, so the converted HTML is sanitised to formatting tags
// only: no attributes, links, images or scripts.
const CONTEXT_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "strong", "em", "del", "code", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6"],
  ALLOWED_ATTR: [],
};

const renderContextHtml = (markdown) =>
  DOMPurify.sanitize(contextConverter.makeHtml(String(markdown)), CONTEXT_SANITIZE_CONFIG);

const precisionCopy = "Precision: reruns can shift by ±2–3 points; priorities don’t change.";

const formatDisplayDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const SummaryDisplay = forwardRef(({ analysisData, isPDFExport = false, isGuestView = false, onUnlockRequest }, ref) => {
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);

  if (!analysisData) {
    return (
      <div className="flex h-64 items-center justify-center rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white">
        <p className="text-sm text-[var(--text-secondary)]">Loading analysis...</p>
      </div>
    );
  }

  const companyName =
    analysisData.company_name ||
    analysisData.company_info?.name ||
    "Modern Slavery Statement";
  const reportYear =
    analysisData.report_year ||
    analysisData.company_info?.report_year ||
    null;
  const overallScore = Math.round(analysisData.overall_score ?? 0);
  const categoryScores = analysisData.category_scores ?? {};
  const strengths = analysisData.key_strengths ?? [];
  const improvements = analysisData.priority_improvements ?? [];
  const recommendations = analysisData.recommendations ?? {};
  const contextSummary = analysisData.context_summary;
  const analysisMetadata = analysisData.analysis_metadata || {};
  const contextHtml = contextSummary
    ? renderContextHtml(contextSummary)
    : null;

  // Framework information from analysis metadata
  const frameworkType =
    analysisMetadata.framework_type ||
    analysisData.framework_type ||
    analysisMetadata.framework?.type ||
    "current";
  const frameworkName =
    analysisMetadata.framework_name ||
    analysisData.framework_name ||
    analysisMetadata.framework?.name ||
    (frameworkType === "advanced" ? "Advanced Framework" : "Current Framework");
  const frameworkQuestions =
    analysisMetadata.framework_questions ||
    analysisData.framework_questions ||
    analysisMetadata.framework?.questions ||
    (frameworkType === "advanced" ? 61 : 48);
  const isAdvancedFramework = frameworkType === "advanced";

  // Worked out from the current benchmark release, not from anything stored with the analysis
  const peers = getAnalysisPeerComparison(analysisData);
  const peerGroups = [peers.industry, peers.index].filter(Boolean);
  const comparedGroups = peerGroups.filter((group) => group.overall);

  const scoreTone = (score) => {
    if (score >= 80) return "text-[var(--accent)]";
    if (score >= 60) return "text-warning-700";
    return "text-danger-700";
  };

  const scoreTitle = (score) => {
    if (score >= 80) return "Strong disclosure";
    if (score >= 60) return "On track";
    if (score >= 40) return "Partial alignment";
    return "Immediate focus";
  };

  const lockedDate = formatDisplayDate(analysisMetadata.computed_at);
  const fingerprint = analysisMetadata.document_hash
    ? analysisMetadata.document_hash.slice(0, 8).toUpperCase()
    : null;
  const metadataParts = [
    analysisMetadata.model_version ? `Model ${analysisMetadata.model_version}` : null,
    lockedDate ? `Locked ${lockedDate}` : null,
    fingerprint ? `Fingerprint ${fingerprint}` : null,
    analysisMetadata.cache_status === "cache-hit" ? "Reused locked score" : null,
  ].filter(Boolean);

  // Guest view: show teaser with locked sections
  if (isGuestView) {
    return (
      <div ref={ref} className="space-y-12">
        {/* Header */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-[rgba(31,76,84,0.16)] bg-white px-4 py-1 text-sm font-semibold text-[var(--accent)]">
              Analysis summary
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
              isAdvancedFramework
                ? "border border-[rgba(31,76,84,0.2)] bg-gradient-to-r from-[rgba(31,76,84,0.1)] to-[rgba(42,100,112,0.1)] text-[var(--accent)]"
                : "border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)] text-primary-500"
            }`}>
              {isAdvancedFramework ? (
                <Sparkles className="h-3 w-3" />
              ) : (
                <Layers className="h-3 w-3" />
              )}
              {frameworkName} • {frameworkQuestions} criteria
            </span>
          </div>
          <div className="space-y-2">
            <h1 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)] md:text-4xl">
              {companyName}
            </h1>
            {reportYear && (
              <p className="text-sm text-[var(--text-secondary)]">
                Reporting period: {reportYear}
              </p>
            )}
          </div>
        </section>

        {/* Overall Score - visible to guests but without benchmark cards */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid gap-8 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/90 p-10 shadow-[0_32px_64px_rgba(15,23,42,0.08)] md:grid-cols-[220px_1fr]"
        >
          <AIProgressRing
            progress={overallScore}
            label="Overall compliance score"
            isStatic={isPDFExport}
            size={isPDFExport ? 150 : 180}
            className={cn("ai-progress-ring", isPDFExport && "ai-progress-ring--pdf")}
          />
          <div className="space-y-4">
            <div className="space-y-2">
              <Tooltip.Provider delayDuration={150}>
                <Tooltip.Root>
                  <div className="flex items-center gap-2">
                    <p className="eyebrow-muted">
                      Overall compliance score
                    </p>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        aria-label="Score precision details"
                        className="rounded-full border border-[rgba(31,76,84,0.2)] p-1 text-[var(--text-soft)] transition hover:text-[var(--text-primary)]"
                      >
                        <Info className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </Tooltip.Trigger>
                  </div>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="top"
                      align="center"
                      className="z-50 max-w-xs rounded-xl border border-[rgba(31,76,84,0.12)] bg-white px-3 py-2 text-xs text-[var(--text-secondary)] shadow-[0_16px_32px_rgba(15,23,42,0.12)]"
                    >
                      {precisionCopy}
                      <Tooltip.Arrow className="fill-white" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
              <p className={cn("text-2xl font-semibold", scoreTone(overallScore))}>
                {scoreTitle(overallScore)}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                {isAdvancedFramework
                  ? "Out of 100 weighted points across governance, disclosure, due diligence, prevention activity, and TISC/international template criteria."
                  : "Out of 100 weighted points across governance, disclosure, due diligence, and prevention activity."}
              </p>
              {metadataParts.length > 0 && (
                <p className="text-xs text-[var(--text-secondary)]">
                  {metadataParts.join(" • ")}
                </p>
              )}
            </div>
            {/* Benchmark cards hidden for guests */}
          </div>
        </motion.section>

        <HomepageCheckNote externalChecks={analysisMetadata.external_checks} />

        {/* Guest teaser: Top gap and top strength */}
        <section className="grid gap-8 md:grid-cols-2">
          <GuestTopGap improvement={improvements[0]} />
          <GuestTopStrength strength={strengths[0]} />
        </section>

        {/* Locked report contents */}
        <LockedReportContents onUnlock={onUnlockRequest} isAdvancedFramework={isAdvancedFramework} />

        {/* Trust block */}
        <TrustBlock />
      </div>
    );
  }

  // Full view for authenticated users
  return (
    <div ref={ref} className="space-y-12">
      {/* Header */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-[rgba(31,76,84,0.16)] bg-white px-4 py-1 text-sm font-semibold text-[var(--accent)]">
            Analysis summary
          </span>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
            isAdvancedFramework
              ? "border border-[rgba(31,76,84,0.2)] bg-gradient-to-r from-[rgba(31,76,84,0.1)] to-[rgba(42,100,112,0.1)] text-[var(--accent)]"
              : "border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)] text-primary-500"
          }`}>
            {isAdvancedFramework ? (
              <Sparkles className="h-3 w-3" />
            ) : (
              <Layers className="h-3 w-3" />
            )}
            {frameworkName} • {frameworkQuestions} criteria
          </span>
        </div>
        <div className="space-y-2">
          <h1 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)] md:text-4xl">
            {companyName}
          </h1>
          {reportYear && (
            <p className="text-sm text-[var(--text-secondary)]">
              Reporting period: {reportYear}
            </p>
          )}
        </div>
      </section>

      {/* Overall Score */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="grid gap-8 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/90 p-10 shadow-[0_32px_64px_rgba(15,23,42,0.08)] md:grid-cols-[220px_1fr]"
      >
        <AIProgressRing
          progress={overallScore}
          label="Overall compliance score"
          isStatic={isPDFExport}
          size={isPDFExport ? 150 : 180}
          className={cn("ai-progress-ring", isPDFExport && "ai-progress-ring--pdf")}
        />
        <div className="space-y-4">
          <div className="space-y-2">
            <Tooltip.Provider delayDuration={150}>
              <Tooltip.Root>
                <div className="flex items-center gap-2">
                  <p className="eyebrow-muted">
                    Overall compliance score
                  </p>
                  <Tooltip.Trigger asChild>
                    <button
                      type="button"
                      aria-label="Score precision details"
                      className="rounded-full border border-[rgba(31,76,84,0.2)] p-1 text-[var(--text-soft)] transition hover:text-[var(--text-primary)]"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </Tooltip.Trigger>
                </div>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="top"
                    align="center"
                    className="z-50 max-w-xs rounded-xl border border-[rgba(31,76,84,0.12)] bg-white px-3 py-2 text-xs text-[var(--text-secondary)] shadow-[0_16px_32px_rgba(15,23,42,0.12)]"
                  >
                    {precisionCopy}
                    <Tooltip.Arrow className="fill-white" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
            <p className={cn("text-2xl font-semibold", scoreTone(overallScore))}>
              {scoreTitle(overallScore)}
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              {isAdvancedFramework
                ? "Out of 100 weighted points across governance, disclosure, due diligence, prevention activity, and TISC/international template criteria."
                : "Out of 100 weighted points across governance, disclosure, due diligence, and prevention activity."}
            </p>
            {metadataParts.length > 0 && (
              <p className="text-xs text-[var(--text-secondary)]">
                {metadataParts.join(" • ")}
              </p>
            )}
          </div>

          {peerGroups.length > 0 && (
            <div className="grid gap-3 md:grid-cols-2">
              {peerGroups.map((group) => (
                <BenchmarkCard key={group.key} group={group} />
              ))}
            </div>
          )}
          {isAdvancedFramework && comparedGroups.length > 0 && (
            <TiscPeerRows groups={comparedGroups} />
          )}
        </div>
      </motion.section>

      <HomepageCheckNote externalChecks={analysisMetadata.external_checks} />

      {comparedGroups.map((group) => (
        <motion.section
          key={group.key}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
        >
          <PercentileBand
            label={`${group.label} benchmark`}
            groupLabel={group.label}
            comparison={group.overall}
          />
        </motion.section>
      ))}

      {/* Detailed Benchmark Comparison - Collapsible */}
      {comparedGroups.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="space-y-4"
        >
          <button
            onClick={() => setShowDetailedComparison(!showDetailedComparison)}
            className="group flex w-full items-center justify-between rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white/80 px-6 py-4 shadow-[0_8px_16px_rgba(15,23,42,0.06)] transition-all hover:border-[rgba(31,76,84,0.18)] hover:shadow-[0_12px_24px_rgba(15,23,42,0.08)]"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-[var(--text-primary)]">
                {showDetailedComparison ? 'Hide' : 'Show'} detailed comparison
              </span>
              <span className="text-xs text-[var(--text-secondary)]">
                Bar charts and category breakdown
              </span>
            </div>
            {showDetailedComparison ? (
              <ChevronUp className="h-5 w-5 text-[var(--accent)] transition-transform group-hover:scale-110" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--accent)] transition-transform group-hover:scale-110" />
            )}
          </button>

          <AnimatePresence>
            {showDetailedComparison && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <BenchmarkComparison analysisData={analysisData} peers={peers} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>
      )}

      {/* TISC Maturity Card - only for Advanced Framework */}
      {isAdvancedFramework && analysisData.tisc_levels && (
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.2 }}
        >
          <TISCMaturityCard
            tiscLevels={analysisData.tisc_levels}
            externalChecks={analysisData.analysis_metadata?.external_checks}
          />
        </motion.section>
      )}

      {/* Strengths and Improvements */}
      <section className="grid gap-8 md:grid-cols-2">
        <InsightList
          title="Key strengths"
          items={strengths}
          emptyLabel="No strengths recorded."
        />
        <ImprovementList improvements={improvements} />
      </section>

      {/* Recommendations */}
      <section className="space-y-4 strategic-actions-section">
        <h2 className="font-display text-xl font-semibold !tracking-display text-[var(--text-primary)]">Strategic actions</h2>
        <div className="grid gap-6 md:grid-cols-3 strategic-actions-grid">
          <RecommendationColumn
            title="Immediate (0-3 months)"
            items={recommendations.immediate}
          />
          <RecommendationColumn
            title="Short term (3-6 months)"
            items={recommendations.short_term}
          />
          <RecommendationColumn
            title="Long term (6-12 months)"
            items={recommendations.long_term}
          />
        </div>
      </section>

      {/* Context */}
      {contextHtml && (
        <section className="space-y-3 rounded-3xl border border-[rgba(31,76,84,0.1)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
          <h2 className="font-display text-xl font-semibold !tracking-display text-[var(--text-primary)]">Context overview</h2>
          <div
            className="space-y-3 text-sm leading-relaxed text-[var(--text-secondary)] [&>p]:mt-0 [&>p]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2"
            dangerouslySetInnerHTML={{ __html: contextHtml }}
          />
        </section>
      )}
    </div>
  );
});

SummaryDisplay.displayName = "SummaryDisplay";

function BenchmarkCard({ group }) {
  const { overall } = group;

  if (!overall) {
    return (
      <div className="rounded-2xl border border-[rgba(31,76,84,0.1)] bg-[var(--accent-light)]/70 p-5">
        <p className="eyebrow-muted">
          {group.label}
        </p>
        <p className="mt-3 text-sm text-[var(--text-secondary)]">
          {group.insufficient
            ? "Not enough comparable statements yet"
            : "Peer comparison isn't available for this analysis"}
        </p>
      </div>
    );
  }

  const atMedian = overall.position === "at_median";
  const delta = overall.user_pct - overall.median_pct;

  return (
    <div className="rounded-2xl border border-[rgba(31,76,84,0.1)] bg-[var(--accent-light)]/70 p-5">
      <p className="eyebrow-muted">
        {group.label} median
      </p>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-2xl font-semibold text-[var(--text-primary)]">
          {overall.median_pct.toFixed(1)}%
        </span>
        <span
          className={cn(
            "text-xs font-semibold",
            delta >= 0 ? "text-[var(--accent)]" : "text-danger-700"
          )}
        >
          {atMedian
            ? "At the median"
            : `You are ${Math.abs(delta).toFixed(1)} pts ${delta > 0 ? "above" : "below"} the ${group.label} median`}
        </span>
      </div>
    </div>
  );
}

// Advanced framework: the TISC criteria (out of 25) compared with the same peer groups
function TiscPeerRows({ groups }) {
  return (
    <div className="rounded-2xl border border-[rgba(31,76,84,0.1)] bg-white/80 p-5">
      <p className="eyebrow-muted">
        TISC criteria compared with peers
      </p>
      <ul className="mt-3 space-y-2 text-sm text-[var(--text-secondary)]">
        {groups.map((group) => (
          <li key={group.key}>
            {group.tisc ? (
              <>
                <span className="font-semibold text-[var(--text-primary)]">
                  TISC score {group.tisc.user_marks}/{group.tisc.max}
                </span>
                {`; ${group.label} median ${formatMarks(group.tisc.median)}/${group.tisc.max}`}
                {group.tisc.position === "at_median" ? " (at the median)" : ""}
                {`. ${describeRank(group.tisc.rank, group.label).text}.`}
              </>
            ) : (
              `${group.label}: TISC comparison isn't available for this analysis.`
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsightList({ title, items, emptyLabel }) {
  return (
    <div className="space-y-4 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
      <h3 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--text-secondary)]">{emptyLabel}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item, index) => (
            <li
              key={`${item}-${index}`}
              className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-[var(--accent-light)]/60 px-4 py-3 text-sm leading-relaxed text-[var(--text-secondary)]"
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ImprovementList({ improvements }) {
  if (!improvements || improvements.length === 0) {
    return (
      <div className="space-y-4 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">Priority improvements</h3>
        <p className="text-sm text-[var(--text-secondary)]">No findings fell below the threshold for a priority improvement.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
      <h3 className="text-lg font-semibold text-[var(--text-primary)]">Priority improvements</h3>
      <ul className="space-y-3">
        {improvements.map((item, index) => (
          <li
            key={`${item.area}-${index}`}
            className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-white px-4 py-3 text-sm leading-relaxed"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="font-medium text-[var(--text-primary)]">{item.area}</p>
                {item.description && (
                  <p className="mt-1 text-xs text-[var(--text-secondary)]">{item.description}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {item.priority && (
                  <span className="rounded-full border border-[rgba(31,76,84,0.15)] bg-[var(--accent-light)] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-500">
                    {item.priority}
                  </span>
                )}
                {item.risk && (
                  <span className="text-[10px] text-[var(--text-secondary)]">{item.risk}</span>
                )}
              </div>
            </div>
            {typeof item.score === "number" && (
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                Current attainment: {item.score.toFixed(1)}%
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecommendationColumn({ title, items }) {
  if (!items || items.length === 0) {
    return (
      <div className="strategic-actions-card rounded-3xl border border-[rgba(31,76,84,0.1)] bg-white/75 p-6">
        <p className="text-sm text-[var(--text-secondary)]">No recommendations in this timeframe.</p>
      </div>
    );
  }

  return (
    <div className="strategic-actions-card rounded-3xl border border-[rgba(31,76,84,0.1)] bg-white/75 p-6 shadow-[0_20px_40px_rgba(15,23,42,0.06)]">
      <p className="eyebrow-muted">
        {title}
      </p>
      <ul className="mt-4 space-y-3 text-sm leading-relaxed text-[var(--text-secondary)]">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-white px-4 py-3">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function GuestTopGap({ improvement }) {
  if (!improvement) {
    return (
      <div className="space-y-3 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
        <p className="eyebrow-muted">
          Top gap identified
        </p>
        <p className="text-sm text-[var(--text-secondary)]">No findings fell below the threshold for a priority improvement.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
      <p className="eyebrow-muted">
        Top gap identified
      </p>
      <div className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-white px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="font-medium text-[var(--text-primary)]">{improvement.area}</p>
            {improvement.description && (
              <p className="mt-1.5 text-sm text-[var(--text-secondary)] leading-relaxed">
                {improvement.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {improvement.priority && (
              <span className="rounded-full border border-[rgba(125,35,35,0.2)] bg-[rgba(125,35,35,0.08)] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-danger-700">
                {improvement.priority}
              </span>
            )}
            {improvement.risk && (
              <span className="text-[10px] text-[var(--text-secondary)]">{improvement.risk}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GuestTopStrength({ strength }) {
  if (!strength) {
    return (
      <div className="space-y-3 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
        <p className="eyebrow-muted">
          Top strength
        </p>
        <p className="text-sm text-[var(--text-secondary)]">Analysis found no standout strengths.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]">
      <p className="eyebrow-muted">
        Top strength
      </p>
      <div className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-[var(--accent-light)]/60 px-4 py-3 text-sm leading-relaxed text-[var(--text-secondary)]">
        {strength}
      </div>
    </div>
  );
}

function LockedReportContents({ onUnlock, isAdvancedFramework }) {
  const lockedItems = [
    "Benchmark against UK peers by industry and FTSE index",
    "Percentile positioning among your peers",
    `Category breakdown (${isAdvancedFramework ? 5 : 4} categories)`,
    "All key strengths (up to 3)",
    `Priority improvements (up to ${isAdvancedFramework ? 7 : 5})`,
    "Strategic actions (immediate, short, long term)",
    "Export as PDF, CSV, JSON",
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/90 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]"
    >
      <div className="flex items-center gap-3 mb-6">
        <Lock className="h-5 w-5 text-[var(--accent)]" />
        <h2 className="font-display text-xl font-semibold !tracking-display text-[var(--text-primary)]">Full report contents</h2>
      </div>

      <ul className="space-y-3 mb-6">
        {lockedItems.map((item, index) => (
          <li key={index} className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
            <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
            {item}
            {index === 0 && (
              <span className="rounded-full bg-[var(--accent)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                Key
              </span>
            )}
          </li>
        ))}
      </ul>

      <Button
        onClick={onUnlock}
        className="w-full gap-2"
        size="lg"
      >
        Unlock full report — sign in with email
      </Button>
    </motion.section>
  );
}

function TrustBlock() {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25 }}
      className="rounded-2xl border border-[rgba(31,76,84,0.1)] bg-[var(--accent-light)]/50 p-6"
    >
      <p className="font-semibold text-[var(--text-primary)] mb-4">How we handle your data</p>
      <ul className="space-y-3">
        <li className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
          <Shield className="h-4 w-4 mt-0.5 text-[var(--accent)] flex-shrink-0" />
          <span>PDF not stored — only analysis results cached</span>
        </li>
        <li className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
          <Server className="h-4 w-4 mt-0.5 text-[var(--accent)] flex-shrink-0" />
          <span>AI analysis in the EU; data stored in London (UK)</span>
        </li>
        <li className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
          <FileCheck className="h-4 w-4 mt-0.5 text-[var(--accent)] flex-shrink-0" />
          <span>Same document = same score (locked result, auditable)</span>
        </li>
      </ul>
    </motion.section>
  );
}

export default SummaryDisplay;
